import type { InvoiceTab } from '../../../utils/invoiceRoutes';
import { isInvoiceImageType } from './incomingInvoice';

export const MAX_INVOICE_UPLOAD_FILES = 10;
export const MAX_INVOICE_UPLOAD_BYTES = 20 * 1024 * 1024;

type InvoiceUploadFileLike = {
  name: string;
  type?: string;
  size: number;
};

export function isAllowedInvoiceUploadFile(file: InvoiceUploadFileLike, tab: InvoiceTab): boolean {
  const name = file.name.toLowerCase();
  const mime = (file.type || '').toLowerCase().split(';')[0].trim();
  if (tab === 'incoming') {
    if (name.endsWith('.pdf') || mime === 'application/pdf') return true;
    if (name.endsWith('.xml') || mime === 'application/xml' || mime === 'text/xml') return true;
    if (isInvoiceImageType({ filename: name, mimeType: mime })) return true;
    return false;
  }
  return name.endsWith('.pdf') || mime === 'application/pdf';
}

export function invoiceUploadFileError(file: InvoiceUploadFileLike, tab: InvoiceTab): string | null {
  if (!isAllowedInvoiceUploadFile(file, tab)) {
    return tab === 'incoming'
      ? `${file.name}: Ungültiges Dateiformat (PDF, XML oder Bild).`
      : `${file.name}: Ungültiges Dateiformat (nur PDF).`;
  }
  if (file.size > MAX_INVOICE_UPLOAD_BYTES) {
    return `${file.name}: Datei zu groß (max. 20 MB).`;
  }
  return null;
}

export function prepareInvoiceUploadFiles<T extends InvoiceUploadFileLike>(
  files: T[],
  tab: InvoiceTab,
): { limited: T[]; toUpload: T[]; errors: string[] } {
  const errors: string[] = [];
  if (files.length > MAX_INVOICE_UPLOAD_FILES) {
    errors.push(
      `Es können maximal ${MAX_INVOICE_UPLOAD_FILES} Dateien auf einmal hochgeladen werden. Die ersten ${MAX_INVOICE_UPLOAD_FILES} wurden übernommen.`,
    );
  }
  const limited = files.slice(0, MAX_INVOICE_UPLOAD_FILES);
  const toUpload: T[] = [];
  for (const file of limited) {
    const fileError = invoiceUploadFileError(file, tab);
    if (fileError) errors.push(fileError);
    else toUpload.push(file);
  }
  return { limited, toUpload, errors };
}
